import Avatar from "@galaxy-io/dls/avatar/Avatar";
import DescriptionList from "@galaxy-io/dls/lists/DescriptionList";
import Text, { TextSize } from "@galaxy-io/dls/text/Text";
import { FontFamily } from "@galaxy-io/dls/theme/enums";
import type { AuditActor, AuditKind } from "@gtm/contracts/core/audit";
import { useQuery } from "@tanstack/react-query";
import { QueryState } from "@/components/query-state";
import { DrawerList } from "@/components/workspace/drawer-content";
import { Inline } from "@/components/workspace/people-styles";
import { apiClient, unwrap } from "@/lib/api-client";
import { date } from "@/lib/utils";

export function useRecordAudit(kind: AuditKind | undefined, ids: string[]) {
  const keys = [...new Set(ids)].sort();
  return useQuery({
    queryKey: ["record-audit", kind, keys],
    enabled: !!kind && !!keys.length,
    queryFn: ({ signal }) =>
      unwrap(
        apiClient().POST("/audit/records", {
          body: { kind: kind!, ids: keys },
          signal,
        }),
      ),
    staleTime: 60000,
  });
}
export function ActorLabel({ actor }: { actor: AuditActor | null | undefined }) {
  return actor ? (
    <Inline>
      <div role="img" aria-label={`${actor.name}'s avatar`}>
        <Avatar seed={actor.name} size={16} />
      </div>
      <Text size={TextSize.BODY_SM}>{actor.name}</Text>
    </Inline>
  ) : (
    <Text size={TextSize.BODY_SM}>Unknown user</Text>
  );
}
export function RecordAudit({
  kind,
  id,
  compact = false,
}: {
  kind: AuditKind;
  id: string;
  compact?: boolean;
}) {
  const result = useRecordAudit(kind, [id]);
  const audit = result.data?.[0];
  if (result.isPending || result.error)
    return (
      <QueryState
        error={result.error}
        loadingMessage="Loading audit…"
        onRetry={() => void result.refetch()}
      />
    );
  if (!audit) return null;
  if (compact)
    return (
      <Inline>
        <Text size={TextSize.BODY_SM}>Created by</Text>
        <ActorLabel actor={audit.createdBy} />
      </Inline>
    );
  const rows = (
    <>
      <DescriptionList
        items={[
          {
            label: "Created by",
            value: (
              <>
                <ActorLabel actor={audit.createdBy} />
              </>
            ),
          },
          { label: "Created", value: date(audit.createdAt, true), family: FontFamily.MONO },
          {
            label: "Updated by",
            value: (
              <>
                <ActorLabel actor={audit.updatedBy} />
              </>
            ),
          },
          { label: "Updated", value: date(audit.updatedAt, true), family: FontFamily.MONO },
        ]}
      />
    </>
  );
  return <DrawerList>{rows}</DrawerList>;
}
